import { McpSiteAssignmentDto, ScheduleSiteChangeRequestAssignmentDto } from "@phrmpc/infrastructure/phrmpc";
import { DateTime } from "luxon";

export interface Schedule {
  id: number;
  date: Date;
  scheduleFrom: string;
  scheduleTo: string;
}

interface ValidationResult {
  id: number;
  message: string;
}

export function validateSchedules(mcpSiteAssignments: McpSiteAssignmentDto[] | ScheduleSiteChangeRequestAssignmentDto[] | []): ValidationResult[] {
  const errors: ValidationResult[] = [];
  const errorKeys = new Set<string>();

  const addError = (id: number, message: string) => {
    const key = `${id}-${message}`;

    if (!errorKeys.has(key)) {
      errorKeys.add(key);
      errors.push({ id, message });
    }
  };

  if (mcpSiteAssignments?.length == 0) {
      addError(1, "Schedules cannot be empty.");
  }

  const setToDaysDate = (
    schedDay: Date | undefined,
    schedTime: string | Date | DateTime | null | undefined,
  ) => {
    if (!schedDay || !schedTime) return "";

    let hours: number;
    let minutes: number;
    let seconds: number;

    if (schedTime instanceof DateTime) {
      hours = schedTime.hour;
      minutes = schedTime.minute;
      seconds = schedTime.second;
    } else if (schedTime instanceof Date) {
      hours = schedTime.getHours();
      minutes = schedTime.getMinutes();
      seconds = schedTime.getSeconds();
    } else {
      [hours, minutes, seconds = 0] = schedTime
        .split(":")
        .map(Number);
    }

    const dayAndTime = new Date(schedDay);

    dayAndTime.setHours(
      hours,
      minutes,
      seconds,
      0,
    );

    return dayAndTime.toISOString();
  };

  const schedules: Schedule[] = mcpSiteAssignments.map((s, i) => ({
    id: i,
    date: s.date ?? new Date(NaN),
    scheduleFrom: setToDaysDate(s.date, s.fromSchedule),
    scheduleTo: setToDaysDate(s.date, s.toSchedule),
  }));

  const parsedSchedules = schedules.map((schedule) => {
    const from = DateTime
      .fromJSDate(new Date(schedule.scheduleFrom))
      .set({ second: 0, millisecond: 0 })
      .toJSDate();

    const to = DateTime
      .fromJSDate(new Date(schedule.scheduleTo))
      .set({ second: 0, millisecond: 0 })
      .toJSDate();

    const date = new Date(schedule.date);

    return {
      ...schedule,
      from,
      to,
      date
    };
  });

  // Field validations
  for (const schedule of parsedSchedules) {
    if (!schedule.scheduleFrom) {
      addError(schedule.id, "Schedule From is required.");
    }

    if (!schedule.scheduleTo) {
      addError(schedule.id, "Schedule To is required.");
    }

    if (
      schedule.scheduleFrom &&
      isNaN(schedule.from.getTime())
    ) {
      addError(schedule.id, "Schedule From must be a valid time.");
    }

    if (
      schedule.scheduleTo &&
      isNaN(schedule.to.getTime())
    ) {
      addError(schedule.id, "Schedule To must be a valid time.");
    }

    if (
      !isNaN(schedule.from.getTime()) &&
      !isNaN(schedule.to.getTime())
    ) {
      // scheduleFrom must be earlier than scheduleTo
      if (schedule.from >= schedule.to) {
        addError(
          schedule.id,
          "Schedule From must be earlier than Schedule To."
        );
      }

      // Cannot span multiple days
      if (
        schedule.from.toDateString() !==
        schedule.to.toDateString()
      ) {
        addError(
          schedule.id,
          "Schedule cannot span multiple days."
        );
      }

      // Check against the schedule date
      if (
        schedule.date.toDateString() !==
        schedule.from.toDateString()
      ) {
        addError(
          schedule.id,
          "Schedule From must belong to the schedule date."
        );
      }

      if (
        schedule.date.toDateString() !==
        schedule.to.toDateString()
      ) {
        addError(
          schedule.id,
          "Schedule To must belong to the schedule date."
        );
      }
    }
  }

  // Duplicate start time check
  const startTimes = new Map<string, number[]>();

  for (const schedule of parsedSchedules) {
    if (!isNaN(schedule.from.getTime())) {
      const key = schedule.from.getTime().toString();

      if (!startTimes.has(key)) {
        startTimes.set(key, []);
      }

      startTimes.get(key)!.push(schedule.id);
    }
  }

  for (const [, ids] of startTimes) {
    if (ids.length > 1) {
      ids.forEach(id =>
        addError(id, "Schedule start time cannot be duplicated.")
      );
    }
  }

  // Overlap validation
  let overlapFound: boolean = false;
  for (let i = 0; i < parsedSchedules.length; i++) {
    if (overlapFound) break;

    const current = parsedSchedules[i];

    if (
      isNaN(current.from.getTime()) ||
      isNaN(current.to.getTime())
    ) {
      continue;
    }

    for (let j = i + 1; j < parsedSchedules.length; j++) {
      const compare = parsedSchedules[j];

      if (
        isNaN(compare.from.getTime()) ||
        isNaN(compare.to.getTime())
      ) {
        continue;
      }

      const overlaps =
        current.from < compare.to &&
        compare.from < current.to;

      if (overlaps) {
        addError(
          current.id,
          "Please check overlapping schedules."
        );
        overlapFound = true;
        break;
      }
    }
  }

  return errors;
}